import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomButton from 'lib/designSystem/Button';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import RiskPremiumCalculator from './RiskPremiumCalculator';

interface NewQuotationDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  newQuotation: any;
  updateNewQuotation: (updates: any) => void;
  rawAmount: string;
  updateRawAmount: (value: string) => void;
  daysDifference: number;
  rutError: string | null;
  dateError: string | null;
  isFormValid: boolean;
  CURRENCIES: any[];
  formattedAmount: string;
  formattedConvertedAmount: string;
  formattedConversionRate: string;
}

const NewQuotationDialog: React.FC<NewQuotationDialogProps> = ({
  open,
  onClose,
  onSubmit,
  newQuotation,
  updateNewQuotation,
  updateRawAmount,
  daysDifference,
  rutError,
  dateError,
  isFormValid,
  CURRENCIES,
  formattedConvertedAmount,
  formattedConversionRate,
  rawAmount,
}) => {
  const [displayAmount, setDisplayAmount] = useState('');

  useEffect(() => {
    if (rawAmount) {
      const num = parseInt(rawAmount, 10);
      if (!isNaN(num)) {
        const formatted = num.toLocaleString('de-DE');
        setDisplayAmount(formatted);
      } else {
        setDisplayAmount('');
      }
    } else {
      setDisplayAmount('');
    }
  }, [rawAmount]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    updateRawAmount(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <CustomDialogTitle setOpen={onClose}>
        Nueva Cotización de Garantía
      </CustomDialogTitle>

      <DialogContent>
        <Grid container spacing={4} flexDirection="column">
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ID Licitación"
                  value={newQuotation.tenderId}
                  onChange={(e) =>
                    updateNewQuotation({ tenderId: e.target.value })
                  }
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="RUT Mandante"
                  value={newQuotation.principalRut}
                  onChange={(e) =>
                    updateNewQuotation({
                      principalRut: e.target.value.toUpperCase(),
                    })
                  }
                  placeholder="12345678-9"
                  error={!!rutError}
                  helperText={rutError || 'Ej: 12345678-9'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de Garantía"
                  value={newQuotation.type}
                  onChange={(e) => updateNewQuotation({ type: e.target.value })}
                >
                  <MenuItem value="offer-seriousness">
                    Seriedad de oferta
                  </MenuItem>
                  <MenuItem value="faithful-compliance">
                    Fiel cumplimiento
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Moneda"
                  value={newQuotation.quotedCurrency}
                  onChange={(e) =>
                    updateNewQuotation({
                      quotedCurrency: e.target.value,
                    })
                  }
                >
                  {CURRENCIES.map((currency) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Monto Cotizado"
                  type="text"
                  value={displayAmount}
                  onChange={handleAmountChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {newQuotation.quotedCurrency}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {newQuotation.quotedCurrency !== 'CLP' &&
                newQuotation.quotedAmount && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Monto Referencial en CLP"
                      value={formattedConvertedAmount}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">CLP</InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Tasa de conversión: 1 {newQuotation.quotedCurrency} ={' '}
                      {formattedConversionRate} CLP
                    </Typography>
                  </Grid>
                )}
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Fecha Inicio"
                    value={newQuotation.startDate}
                    onChange={(date) => updateNewQuotation({ startDate: date })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Fecha Término"
                    value={newQuotation.endDate}
                    onChange={(date) => updateNewQuotation({ endDate: date })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!dateError,
                        helperText: dateError,
                      },
                    }}
                  />
                </LocalizationProvider>
                {!dateError && daysDifference !== 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Duración de la garantía: {daysDifference} días
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <RiskPremiumCalculator
              amount={parseFloat(newQuotation.quotedAmount) || 0}
              days={daysDifference}
              conversionRate={newQuotation.conversionRate}
              type={newQuotation.type}
              quotedCurrency={newQuotation.quotedCurrency}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={onSubmit}
          disabled={!isFormValid}
        >
          Crear Cotización
        </CustomButton>
      </Box>
    </Dialog>
  );
};

export default NewQuotationDialog;
